import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { Stack } from '@mui/material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';

import ResultGrid from './ResultGrid';
import {
  AvailableFiltersSchema,
  PatientHPOListsSchema,
  savedFilterResults,
} from '@/zod_schemas';
import { Columns, FiltersState, Rows } from './types';
import { useGetAnnotationSource } from '@/api/annotationSource';
import {
  useAddVariants,
  useGetAvailableFilters,
  useGetFilterOutput,
  useGetOutputAnnotationTable,
  useGetPatientHPO,
  useRemoveVariants,
} from '@/api/result/result';
import FilterLayout from './Interpretation/FilterLayout';

interface ResultFiltrationProps {
  outputID: string;
  allowValidateVariant: boolean;
}

const ResultFiltration = ({
  outputID,
  allowValidateVariant,
}: ResultFiltrationProps) => {
  // Filters available from server
  const [availableFilters, setAvailableFilters] =
    useState<AvailableFiltersSchema>({
      vcf_filters: [],
      annotation_filters: [],
      hpo_filters: [],
    });

  // Grid data
  const [rowsData, setRows] = useState<Rows[]>([]);
  const [columns, setColumns] = useState<Columns[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);

  // Pagination / cursors
  const [cursor, setCursor] = useState<string | null>(null);
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  // Final filters built from the panel
  const [finalFilters, setFinalFilters] = useState<FiltersState>({});

  // MUI DataGrid pagination model
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  // Selection states
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [selectedVariantRows, setSelectedVariantRows] =
    useState<GridRowSelectionModel>([]);

  // Sync cursor to trigger fetch (controlled)
  useEffect(() => {
    if (shouldFetch) {
      setCurrentCursor(cursor);
      setShouldFetch(false);
    }
  }, [cursor, shouldFetch]);

  // Fetch lists (annotation sources, patient HPO, available filters)
  const { data: annotationSources = [], isLoading: sourcesLoading } =
    useGetAnnotationSource();
  const { data: filterOutput } = useGetFilterOutput(outputID);
  const { data: patientHPO, refetch: refchPatientHPO } =
    useGetPatientHPO(outputID);
  const [patientHpoList, setPatientHPOList] = useState<PatientHPOListsSchema>(
    patientHPO || [],
  );

  useEffect(() => {
    if (patientHPO) setPatientHPOList(patientHPO);
  }, [patientHPO]);

  const { data: availableFiltersData } = useGetAvailableFilters();
  useEffect(() => {
    if (availableFiltersData) setAvailableFilters(availableFiltersData);
  }, [availableFiltersData]);

  // Variants mutations
  const addVariants = useAddVariants();
  const removeVariants = useRemoveVariants();

  // Output table (depends on filters + cursor)
  const {
    data: outputData,
    isLoading: dataLoading,
    refetch,
  } = useGetOutputAnnotationTable(
    finalFilters,
    outputID,
    [],
    currentCursor || undefined,
  );

  // Build columns/rows when output data changes
  const hideColumns = useMemo(() => new Set(['id']), []);
  const excludeColumns = useMemo(() => new Set<string>(['']), []);

  useEffect(() => {
    if (!outputData) return;

    const { headers, rows, next } = outputData.results as {
      headers: string[];
      rows: Record<string, string | number | null>[];
      next: string | null;
    };

    const generatedColumns: Columns[] = headers
      .filter((h) => !excludeColumns.has(h) && !hideColumns.has(h))
      .map((h) => ({
        field: h,
        headerName: h.replace(/_/g, ' '),
        minWidth: 120,
        maxWidth: 200,
        flex: 1,
      }));

    const formattedRows: Rows[] = rows.map((row) =>
      Object.entries(row).reduce((acc, [key, value]) => {
        if (!excludeColumns.has(key)) {
          acc[key] = value == null ? '-' : value;
        }
        return acc;
      }, {} as Rows),
    );

    setColumns(generatedColumns);
    setRows(formattedRows);
    setCursor(next);
    setRowCount(outputData.row_count || 0);
    setLoading(false);
  }, [outputData, hideColumns, excludeColumns]);

  // Cursor helpers
  const extractCursor = useCallback((url: string | null): string | null => {
    if (!url) return null;
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.searchParams.get('cursor');
    } catch {
      return null;
    }
  }, []);

  const handlePaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      setPaginationModel(model);

      const newCursor =
        model.page > paginationModel.page
          ? extractCursor(outputData?.next ?? null)
          : extractCursor(outputData?.previous ?? null);

      if (newCursor) {
        setCursor(newCursor);
        setShouldFetch(true);
      }
    },
    [
      extractCursor,
      outputData?.next,
      outputData?.previous,
      paginationModel.page,
    ],
  );

  // Mutations handlers
  const onAddVariant = useCallback(async () => {
    if (selectedRows.length <= 0) {
      enqueueSnackbar('Please select at least 1 variant', { variant: 'info' });
      return;
    }
    try {
      await addVariants.mutateAsync({
        outputId: outputID,
        variantIds: selectedRows as string[],
      });
      enqueueSnackbar('Variant successfully saved', { variant: 'success' });
      refetch();
    } catch {
      enqueueSnackbar('Failed to save variant', { variant: 'error' });
      refetch();
    }
  }, [addVariants, outputID, selectedRows, refetch]);

  const onRemoveVariant = useCallback(async () => {
    if (selectedRows.length <= 0) {
      enqueueSnackbar('Please select at least 1 variant', { variant: 'info' });
      return;
    }
    try {
      await removeVariants.mutateAsync({
        outputId: outputID,
        variantIds: selectedRows as string[],
      });
      enqueueSnackbar('Variant successfully removed', { variant: 'success' });
      refetch();
    } catch {
      enqueueSnackbar('Failed to remove variant', { variant: 'error' });
      refetch();
    }
  }, [removeVariants, outputID, selectedRows, refetch]);

  // Form
  const methods = useForm<z.infer<typeof savedFilterResults>>({
    resolver: zodResolver(savedFilterResults),
    defaultValues: filterOutput, // may be undefined initially
  });

  // If filter defaults arrive late, reset form so UI reflects them
  useEffect(() => {
    if (filterOutput) methods.reset(filterOutput);
  }, [filterOutput, methods]);

  return (
    <Stack direction="row" sx={{ height: '100%' }}>
      <FormProvider {...methods}>
        <FilterLayout
          availableFilters={availableFilters}
          sourcesLoading={sourcesLoading}
          annotationSources={annotationSources}
          setFinalFilters={setFinalFilters}
          patientHpoList={patientHpoList}
          setSelectedRows={setSelectedRows}
          setSelectedVariantRows={setSelectedVariantRows}
          selectedVariantRows={selectedVariantRows}
          refchPatientHPO={refchPatientHPO}
        />

        <ResultGrid
          rows={rowsData}
          columns={columns}
          loading={loading}
          dataLoading={dataLoading}
          rowCount={rowCount}
          paginationModel={paginationModel}
          handlePaginationModelChange={handlePaginationModelChange}
          onAddVariant={onAddVariant}
          onRemoveVariant={onRemoveVariant}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
          outputID={outputID}
          allowValidateVariant={allowValidateVariant}
        />
      </FormProvider>
    </Stack>
  );
};

export default ResultFiltration;
